import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'nycd-theme';

function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const initial = stored || 'dark';
    applyTheme(initial);
    return initial;
  });

  const setTheme = useCallback((newTheme) => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
    applyTheme(newTheme);
    // Persist to backend (fire and forget)
    api.put('/auth/preferences', { theme_preference: newTheme }).catch(() => {});
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  // Sync from backend user data when available
  const syncFromUser = useCallback((userTheme) => {
    if (userTheme && !localStorage.getItem(STORAGE_KEY)) {
      setThemeState(userTheme);
      localStorage.setItem(STORAGE_KEY, userTheme);
      applyTheme(userTheme);
    }
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, syncFromUser }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
